import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';

const Documentation = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSection, setActiveSection] = useState('overview');

    useEffect(() => {
        window.scrollTo(0, 0);

        const handleScroll = () => {
            const sections = ['overview', 'getting-started', 'features', 'dns-verification', 'dashboard-guide', 'security-privacy', 'faq'];
            const scrollPos = window.scrollY + 100;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element && scrollPos >= element.offsetTop && scrollPos < element.offsetTop + element.offsetHeight) {
                    setActiveSection(section);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);



    const sections = [
        { id: 'overview', title: 'Overview', icon: 'la-info-circle' },
        { id: 'getting-started', title: 'Getting Started', icon: 'la-rocket' },
        { id: 'features', title: 'Feature Guide', icon: 'la-star' },
        { id: 'dns-verification', title: 'DNS Verification', icon: 'la-shield-alt' },
        { id: 'dashboard-guide', title: 'Dashboard Guide', icon: 'la-desktop' },
        { id: 'security-privacy', title: 'Security & Privacy', icon: 'la-lock' },
        { id: 'faq', title: 'FAQ', icon: 'la-question-circle' },
    ];

    const filteredSections = sections.filter(s => 
        s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <PageLayout title="Documentation">
            <div className="row g-4">
                {/* Sidebar Navigation */}
                <div className="col-lg-3 d-none d-lg-block">
                    <div className="sticky-top pt-2" style={{ top: '80px', zIndex: 10 }}>
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="card-header bg-primary text-white py-3 border-0">
                                <h6 className="mb-0 fw-bold">Documentation Nav</h6>
                            </div>
                            <div className="list-group list-group-flush py-2">
                                {sections.map(section => (
                                    <a 
                                        key={section.id}
                                        href={`#${section.id}`}
                                        className={`list-group-item list-group-item-action border-0 px-4 py-3 d-flex align-items-center ${activeSection === section.id ? 'bg-primary bg-opacity-10 text-primary fw-bold active' : 'text-muted'}`}
                                    >
                                        <i className={`las ${section.icon} fs-5 me-3`}></i>
                                        {section.title}
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div className="mt-4 p-3 bg-light rounded-4 border border-light">
                            <p className="small text-muted mb-2">Need direct support?</p>
                            <a href="mailto:support@secure360.us" className="btn btn-outline-primary btn-sm w-100 rounded-pill">Contact Support</a>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="col-lg-9">
                    {/* Search Bar */}
                    <div className="card border-0 shadow-sm mb-5 rounded-4 overflow-hidden bg-white">
                        <div className="card-body p-4">
                            <div className="input-group input-group-lg bg-light border-0 rounded-pill px-3 shadow-sm">
                                <span className="input-group-text bg-transparent border-0 text-muted">
                                    <i className="las la-search fs-4"></i>
                                </span>
                                <input 
                                    type="text" 
                                    className="form-control bg-transparent border-0 fs-6 shadow-none" 
                                    placeholder="Search documentation, features, guides..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {filteredSections.length === 0 && (
                        <div className="text-center py-5 animation-fade-in">
                            <i className="las la-search-minus fa-4x text-muted mb-3 opacity-50"></i>
                            <h4 className="text-muted">No matching topics found</h4>
                            <p className="text-secondary">Try searching for "DNS", "Dashboard", or "Security".</p>
                            <button className="btn btn-link" onClick={() => setSearchTerm('')}>Clear search</button>
                        </div>
                    )}

                    {/* Overview */}
                    <section id="overview" className="mb-5 animation-fade-in" style={{ display: filteredSections.some(s => s.id === 'overview') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-info-circle fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Welcome to Secure360</h2>
                        </div>
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                            <div className="card-body p-4">
                                <p className="lead text-secondary">
                                    How secure is your digital presence? In today's interconnected world, safeguarding your website isn't just about code—it's about building trust with your visitors and protecting your online identity.
                                </p>
                                <p className="text-secondary">
                                    Secure360 is an all-in-one web security and monitoring platform designed to simplify complex protection. We continuously audit your server setup, inspect encryption standards, and monitor for unauthorized changes, translating technical data into clear, simple, and actionable steps.
                                </p>
                                <div className="row g-4 mt-2">
                                    <div className="col-md-6">
                                        <div className="h-100 p-3 bg-light rounded-3 border-start border-4 border-primary shadow-sm">
                                            <h6 className="fw-bold mb-2">Simplicity at Core</h6>
                                            <p className="small text-muted mb-0">No technical background required. We turn complex data into clear, easy-to-understand grades.</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="h-100 p-3 bg-light rounded-3 border-start border-4 border-success shadow-sm">
                                            <h6 className="fw-bold mb-2">Continuous Protection</h6>
                                            <p className="small text-muted mb-0">Automatic checks monitor your sites round-the-clock so you can focus on growing your business.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Getting Started */}
                    <section id="getting-started" className="mb-5" style={{ display: filteredSections.some(s => s.id === 'getting-started') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-rocket fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Getting Started</h2>
                        </div>
                        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                            <p className="text-secondary mb-4">
                                Securing your website with Secure360 is straightforward. In just four simple steps, you can set up automated monitoring and gain full visibility into your website's security health.
                            </p>
                            
                            <div className="mb-4">
                                <h5 className="fw-bold text-dark d-flex align-items-center mb-2">
                                    <span className="badge bg-primary me-2">1</span> Create Your Account
                                </h5>
                                <p className="text-muted ms-4">
                                    Register on our platform using your professional email address or log in instantly using Google authentication. This ensures only you can manage your website's security details.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5 className="fw-bold text-dark d-flex align-items-center mb-2">
                                    <span className="badge bg-primary me-2">2</span> Add Your Domain
                                </h5>
                                <p className="text-muted ms-4">
                                    Navigate to your dashboard and enter the address of the website you want to monitor (for example: <code>yourwebsite.com</code>).
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5 className="fw-bold text-dark d-flex align-items-center mb-2">
                                    <span className="badge bg-primary me-2">3</span> Verify Domain Ownership
                                </h5>
                                <p className="text-muted ms-4">
                                    To protect public privacy, we require verification before conducting deep scans. You'll add a simple verification code to your domain's DNS settings (see the <a href="#dns-verification" className="text-primary fw-semibold">DNS Verification Guide</a> below).
                                </p>
                            </div>

                            <div>
                                <h5 className="fw-bold text-dark d-flex align-items-center mb-2">
                                    <span className="badge bg-primary me-2">4</span> Start Auditing & Monitoring
                                </h5>
                                <p className="text-muted ms-4">
                                    Once verified, click "Scan" to run your first check. Within seconds, Secure360 will analyze your site and generate a detailed report with a letter grade and helpful tips.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Features */}
                    <section id="features" className="mb-5" style={{ display: filteredSections.some(s => s.id === 'features') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-star fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Feature Guide</h2>
                        </div>
                        <div className="row g-4">
                            {[
                                { title: 'SSL/TLS Certificate Audit', desc: 'Checks your website\'s encryption to ensure visitor connections are safe and private, helping you avoid scary "Not Secure" browser warning screens.', benefit: 'Keeps visitor trust high.' },
                                { title: 'Digital Entry Checks', desc: 'Inspects key server ports to ensure no security doors are left unlocked, keeping malicious intruders from scanning and exploiting server weaknesses.', benefit: 'Closes backdoor access.' },
                                { title: 'Smart Security Grading', desc: 'Translates complex technical measurements into clear A-to-F letter grades so you immediately know the security status of your site.', benefit: 'Understand results instantly.' },
                                { title: 'Traffic Surge Alerts', desc: 'Monitors the flow of data coming to your website and warns you if a volumetric bot attack (DoS) is trying to flood and crash your servers.', benefit: 'Minimizes site downtime.' },
                                { title: 'System File Protection', desc: 'Constantly checks vital files on your site to make sure they haven\'t been modified, added to, or deleted without your consent.', benefit: 'Detects stealthy hacks.' },
                                { title: 'Visual Access Mapping', desc: 'Displays login activity on an interactive map, helping you quickly spot and flag unusual login attempts originating from unexpected locations.', benefit: 'Track physical access.' }
                            ].map((f, i) => (
                                <div className="col-md-6" key={i}>
                                    <div className="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition-all">
                                        <div className="card-body p-4">
                                            <h6 className="fw-bold text-primary mb-2">{f.title}</h6>
                                            <p className="small text-dark mb-3">{f.desc}</p>
                                            <div className="p-2 bg-light rounded small text-muted border-start border-3 border-primary">
                                                <strong>Core Benefit:</strong> {f.benefit}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* DNS Verification */}
                    <section id="dns-verification" className="mb-5" style={{ display: filteredSections.some(s => s.id === 'dns-verification') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-shield-alt fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Domain Verification</h2>
                        </div>
                        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                            <p className="text-secondary mb-4">
                                Why is verification required? To prevent unauthorized scanning of public systems, Secure360 requires domain ownership verification. This ensures that only authorized administrators can request and view deep security insights.
                            </p>
                            
                            <div className="row g-4 mb-4">
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded text-center border h-100 d-flex flex-column justify-content-between">
                                        <div>
                                            <span className="badge bg-primary mb-2">Step 1</span>
                                            <h6 className="fw-bold">Copy Your Token</h6>
                                        </div>
                                        <p className="small text-muted mb-0 mt-2">Go to settings and copy your unique verification text (e.g., <code>secure360-verification=TOKEN</code>).</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded text-center border h-100 d-flex flex-column justify-content-between">
                                        <div>
                                            <span className="badge bg-primary mb-2">Step 2</span>
                                            <h6 className="fw-bold">Create TXT Record</h6>
                                        </div>
                                        <p className="small text-muted mb-0 mt-2">Access your domain registrar (GoDaddy, Namecheap, etc.) and add a new TXT record with host <code>@</code>.</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded text-center border h-100 d-flex flex-column justify-content-between">
                                        <div>
                                            <span className="badge bg-primary mb-2">Step 3</span>
                                            <h6 className="fw-bold">Activate Monitoring</h6>
                                        </div>
                                        <p className="small text-muted mb-0 mt-2">Save DNS changes. Secure360 will automatically find the record, verify your site, and begin monitoring.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 bg-light rounded border mb-4">
                                <h6 className="fw-bold mb-2 small text-uppercase text-muted">Example DNS Configuration</h6>
                                <p className="small text-muted mb-2">Create a new DNS record with the following values:</p>
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered align-middle mb-0 small bg-white text-dark">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Record Type</th>
                                                <th>Host / Name</th>
                                                <th>Value / Text Content</th>
                                                <th>TTL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><code>TXT</code></td>
                                                <td><code>@</code> (or your domain name)</td>
                                                <td><code>secure360-verification=a1b2c3d4e5f6...</code></td>
                                                <td><code>Automatic</code> (or <code>3600</code> / 1 hour)</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <h6 className="fw-bold mb-3 text-dark">Understanding Connection Status:</h6>
                            <div className="list-group list-group-flush mb-2">
                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                    <span><span className="badge bg-success me-2">Connected</span> Ownership is verified. Active scanning and historical monitoring are active.</span>
                                    <i className="las la-check-circle text-success fs-4"></i>
                                </div>
                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                    <span><span className="badge bg-warning text-dark me-2">Pending</span> Waiting for DNS settings to update online. Initial scans are locked.</span>
                                    <i className="las la-clock text-warning fs-4"></i>
                                </div>
                                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                                    <span><span className="badge bg-danger me-2">Failed</span> Verification lost or expired. Active scans are paused until ownership is re-verified.</span>
                                    <i className="las la-times-circle text-danger fs-4"></i>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Dashboard Guide */}
                    <section id="dashboard-guide" className="mb-5" style={{ display: filteredSections.some(s => s.id === 'dashboard-guide') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-desktop fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Dashboard Guide</h2>
                        </div>
                        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                            <p className="text-secondary mb-4">
                                Once logged in, your central dashboard displays a complete summary of your monitored digital assets. Here is what you will find:
                            </p>
                            
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <h6 className="fw-bold text-dark mb-2">
                                            <i className="las la-file-pdf text-primary me-2"></i>Recent Generated Reports
                                        </h6>
                                        <p className="small text-muted mb-0">Lists your latest scans, showing the tested domain, the safety grade, the numeric score, and a button to open a complete, shareable PDF audit report.</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <h6 className="fw-bold text-dark mb-2">
                                            <i className="las la-globe text-primary me-2"></i>Monitored Domains
                                        </h6>
                                        <p className="small text-muted mb-0">Shows the total number of websites added to your dashboard, allowing you to see your footprint at a glance.</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <h6 className="fw-bold text-dark mb-2">
                                            <i className="las la-server text-primary me-2"></i>System Status
                                        </h6>
                                        <p className="small text-muted mb-0">Real-time indicators showing whether your websites are currently online, responding to visitors, or experiencing unexpected server outages.</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <h6 className="fw-bold text-dark mb-2">
                                            <i className="las la-lock text-primary me-2"></i>SSL Health
                                        </h6>
                                        <p className="small text-muted mb-0">Quick counts showing how many domains have fully valid certificates, and warning you if any are expiring soon so you can renew them in time.</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <h6 className="fw-bold text-dark mb-2">
                                            <i className="las la-bug text-primary me-2"></i>Malware Risks
                                        </h6>
                                        <p className="small text-muted mb-0">Tracks and warns you if any monitored domain is flagged with critical, immediate security risks that require configuration updates or server cleanup.</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <h6 className="fw-bold text-dark mb-2">
                                            <i className="las la-shield-alt text-primary me-2"></i>Average Security Score
                                        </h6>
                                        <p className="small text-muted mb-0">Aggregates and averages scores across all your monitored domains (out of 100) to give you an overview of your organization's security posture.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Security & Privacy */}
                    <section id="security-privacy" className="mb-5" style={{ display: filteredSections.some(s => s.id === 'security-privacy') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-lock fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Security & Privacy</h2>
                        </div>
                        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                            <p className="text-secondary mb-3">
                                At Secure360, your trust is paramount. Our platform is designed with multi-layer encryption and a strict Zero-Trust approach. Here is how we keep your information secure:
                            </p>
                            <ul className="text-muted small mb-0 ps-3">
                                <li className="mb-2"><strong>Data Encryption:</strong> All account details, domain statistics, and reports are protected both in transit (using TLS 1.3) and at rest (using AES-256 encryption standards).</li>
                                <li className="mb-2"><strong>Zero-Trust Auditing:</strong> We do not scan domains without explicit DNS verification, preventing unauthorized scouting and misuse.</li>
                                <li className="mb-2"><strong>Privacy First:</strong> Secure360 monitors structural security, certificates, and port responses. We do not track or store personal data from your website's visitors, ensuring complete privacy compliance.</li>
                                <li><strong>Secure Integrations:</strong> Our server monitoring agents use secure tokens and transmit data unidirectionally, meaning they can report health status but cannot receive commands or compromise your server.</li>
                            </ul>
                        </div>
                    </section>

                    {/* FAQ */}
                    <section id="faq" className="mb-5" style={{ display: filteredSections.some(s => s.id === 'faq') ? 'block' : 'none' }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary text-white rounded-circle p-2 me-3 shadow-sm">
                                <i className="las la-question-circle fs-3"></i>
                            </div>
                            <h2 className="fw-bold mb-0 text-dark">Frequently Asked Questions</h2>
                        </div>
                        <div className="accordion border-0 shadow-sm rounded-4 overflow-hidden" id="faqAccordion">
                            {[
                                { q: 'How long does domain DNS verification take?', a: 'Once you add the TXT record, verification is usually completed in under 15 minutes. However, DNS records can sometimes take up to 24 hours to propagate worldwide depending on your DNS host and TTL settings.' },
                                { q: 'What happens if I remove my verification TXT record?', a: 'Our system runs a periodic verification check. If the record is removed, the domain status changes to "Failed" and active security checks are paused. Simply re-add the record to restore monitoring.' },
                                { q: 'Can I monitor multiple subdomains?', a: 'Yes. Every subdomain (e.g., blog.yoursite.com, shop.yoursite.com) can be registered individually and verified using its own unique token.' },
                                { q: 'Does Secure360 impact my website\'s performance or load speed?', a: 'Not at all. Our scanning queries are lightweight and non-invasive. They analyze configuration details and public certificates without putting stress on your servers or affecting your real visitors.' },
                                { q: 'How is the A to F security grade calculated?', a: 'The grade represents your site\'s overall resilience. It takes into account key areas like SSL validity, expired or weak certificates, open or vulnerable network ports, and overall uptime health.' },
                                { q: 'How do I download a report to share with my developer?', a: 'From your main dashboard or individual domain view, click the "PDF" button. This generates a clean, comprehensive document that you can easily share with your web developer or hosting support team to resolve identified issues.' }
                            ].map((item, i) => (
                                <div className="accordion-item border-0 border-bottom" key={i}>
                                    <h2 className="accordion-header">
                                        <button className="accordion-button collapsed fw-bold py-4 px-4 shadow-none" type="button" data-bs-toggle="collapse" data-bs-target={`#faq${i}`}>
                                            {item.q}
                                        </button>
                                    </h2>
                                    <div id={`faq${i}`} className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                        <div className="accordion-body text-muted px-4 pb-4">
                                            {item.a}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </PageLayout>
    );
};

export default Documentation;
